/**
 * The course on Desk (#1393 M2, contract 5): one heading, a two-option mode
 * control and the mode's content. Outline groups by recorded relationships;
 * Calendar order lists sessions by date. The mode lives in the address
 * (`?order=calendar`, replaced, never pushed). Only the shown mode is mounted,
 * so no record has a second writable controller in a hidden mode beside a
 * visit's owner. A switch is a router replace, so the leave coordinator asks
 * before it discards unsaved work or a write in flight; what the person did
 * that an address doesn't carry (find, Cancelled only, the bulk selection,
 * open topics) is kept in the Desk's live reading and the remounted mode
 * starts from it. The Google Calendar row is drawn once, beside both
 * modes: one writable controller for that record.
 */
import { useEffect, useLayoutEffect, useRef, type KeyboardEvent } from "react";
import { useLocation, useSearchParams } from "react-router";

import { deskModeSearch } from "@/navigation/course-address";
import type { CourseWorkspaceShell } from "@/navigation/routes";
import { AllSessionsRegister } from "../AllSessionsRegister";
import { CourseGoogleCalendarFrame } from "../CourseGoogleCalendar";
import { CoursePlan } from "../CoursePlan";
import { CourseOutline } from "./CourseOutline";

export const THE_COURSE_ID = "the-course";
type Mode = "outline" | "calendar";
const MODES: readonly { mode: Mode; label: string }[] = [{ mode: "outline", label: "Outline" }, { mode: "calendar", label: "Calendar order" }];

export function TheCourse({ courseId, courseHref, teaching, canEdit, termOpen, timezone, shell }: {
  courseId: string;
  courseHref: string;
  teaching: boolean;
  canEdit: boolean;
  termOpen: boolean;
  timezone: string;
  shell: CourseWorkspaceShell;
}) {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const mode: Mode = params.get("order") === "calendar" ? "calendar" : "outline";
  // A pasted or reloaded `?order=calendar&visit=X` has no visit memory: the
  // visit's hosts live in Outline, so the address is normalized there (R62).
  const normalized = useRef(false);
  useEffect(() => {
    if (normalized.current) return;
    normalized.current = true;
    const memory = location.state && typeof location.state === "object" && "imamVisit" in (location.state as Record<string, unknown>);
    if (mode === "calendar" && params.has("visit") && !memory) setParams(deskModeSearch(params, "outline"), { replace: true, state: location.state });
  }, [mode, params, setParams, location.state]);
  // The switch keeps the area's top edge where it was, so the page never jumps.
  const area = useRef<HTMLElement>(null);
  const edge = useRef<number | null>(null);
  useLayoutEffect(() => {
    if (edge.current === null || !area.current) return;
    const delta = area.current.getBoundingClientRect().top - edge.current;
    edge.current = null;
    if (delta) window.scrollBy(0, delta);
  }, [mode]);
  const choose = (next: Mode) => {
    if (next === mode) return;
    edge.current = area.current?.getBoundingClientRect().top ?? null;
    // Each mode's selectors leave with it; the rows the person browsed live in Desk memory.
    setParams(deskModeSearch(params, next), { replace: true, state: location.state });
  };
  const radios = useRef<Record<Mode, HTMLButtonElement | null>>({ outline: null, calendar: null });
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    const index = MODES.findIndex((item) => item.mode === mode);
    const next = event.key === "Home" ? 0 : event.key === "End" ? MODES.length - 1
      : (index + (event.key === "ArrowLeft" || event.key === "ArrowUp" ? -1 : 1) + MODES.length) % MODES.length;
    choose(MODES[next].mode);
    radios.current[MODES[next].mode]?.focus();
  };

  return (
    <section ref={area} aria-labelledby={`${THE_COURSE_ID}-heading`} id={THE_COURSE_ID} tabIndex={-1} className="grid gap-3 pt-6 focus-visible:outline-none" data-the-course="">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id={`${THE_COURSE_ID}-heading`} className="text-section font-semibold">The course</h2>
        <div role="radiogroup" aria-label="Show the course as" className="inline-flex rounded-control border border-rule bg-card p-0.5" onKeyDown={onKeyDown}>
          {MODES.map((item) => (
            <button key={item.mode} ref={(node) => { radios.current[item.mode] = node; }} type="button" role="radio" aria-checked={mode === item.mode} tabIndex={mode === item.mode ? 0 : -1}
              data-mode={item.mode}
              className="min-h-[var(--control-height-compact)] rounded-[calc(var(--radius-control)-2px)] px-3 text-ui pointer-coarse:min-h-11 aria-checked:bg-primary aria-checked:text-primary-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              onClick={() => choose(item.mode)}>
              {item.label}
            </button>
          ))}
        </div>
      </div>
      {mode === "outline" ? (
        <div data-course-mode="outline">
          <CourseOutline courseId={courseId} courseHref={courseHref} teaching={teaching} canEdit={canEdit} termOpen={termOpen} timezone={timezone} />
        </div>
      ) : (
        <div data-course-mode="calendar">
          {teaching
            ? <CoursePlan courseId={courseId} timezone={timezone} termOpen={termOpen} canEdit={canEdit} shell={shell} portalEnabled={false} headingLevel="h3" emptySignal calendarOrder />
            : <AllSessionsRegister courseId={courseId} timezone={timezone} teaching={false} termOpen={termOpen} shell={shell} emptySignal calendarOrder />}
        </div>
      )}
      <CourseGoogleCalendarFrame courseId={courseId} />
    </section>
  );
}
